import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Shirt, PlusCircle, ArrowLeft, Eye } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/components/ui/use-toast';
import DataTable from '@/components/DataTable';
import Pagination from '@/components/Pagination';
import SelectGarmentTypeModal from '@/components/solicitudes/SelectGarmentTypeModal';
import { useAuth } from '@/contexts/AuthContext';
import DetalleSolicitudModal from '@/components/solicitudes/DetalleSolicitudModal';

const ITEMS_PER_PAGE = 10;

const SolicitudLenceriaPage = () => {
  const navigate = useNavigate();
  const { edificioId } = useParams(); // Obtener edificioId de la ruta
  const location = useLocation();
  const { toast } = useToast();
  const [displayedData, setDisplayedData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const { user } = useAuth();
  
  // Estados para modal de detalle
  const [selectedSolicitud, setSelectedSolicitud] = useState(null);
  const [isDetalleModalOpen, setIsDetalleModalOpen] = useState(false);

  // Obtener edificio del state (si viene de HospedajesTable)
  const edificioFromState = location.state?.edificio;

  const columns = [
    {
      id: 'createdAt',
      label: 'Fecha',
      sortable: true,
      render: (row) => new Date(row.createdAt).toLocaleDateString('es-ES', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    },
    {
      id: 'creator',
      label: 'Solicitante',
      sortable: true,
      render: (row) => row.creator?.nombre || '-'
    },
    {
      id: 'edificio',
      label: 'Portafolio',
      sortable: true,
      render: (row) => (
        <div>
          <p className="font-medium">{row.edificio?.nombre}</p>
          <p className="text-xs text-gray-500">{row.edificio?.ciudad}</p>
        </div>
      )
    },
    {
      id: 'total_solicitado',
      label: 'Total Solicitado',
      sortable: true,
      render: (row) => (
        <Badge variant="outline">{row.total_solicitado} unidades</Badge>
      )
    },
    {
      id: 'total_entregado',
      label: 'Total Entregado',
      sortable: true,
      render: (row) => (
        <Badge variant={row.total_entregado === row.total_solicitado ? 'default' : 'secondary'}>
          {row.total_entregado} unidades
        </Badge>
      )
    },
    {
      id: 'estado',
      label: 'Estado',
      sortable: true,
      render: (row) => {
        const colors = {
          'Pendiente': 'bg-yellow-100 text-yellow-800',
          'En Proceso': 'bg-blue-100 text-blue-800',
          'Completada': 'bg-green-100 text-green-800',
          'Cancelada': 'bg-red-100 text-red-800'
        };
        return (
          <Badge className={colors[row.estado] || 'bg-gray-100 text-gray-800'}>
            {row.estado}
          </Badge>
        );
      }
    },
    {
      id: 'items',
      label: 'Items',
      render: (row) => (
        <span className="text-sm text-gray-600">{row.items?.length || 0} tipo(s)</span>
      )
    },
    {
      id: 'actions',
      label: 'Acciones',
      actions: [
        {
          key: 'ver-detalle-lenceria-page',
          icon: 'Eye',
          tooltip: 'Ver Detalle',
          className: 'text-purple-600 hover:bg-purple-50'
        }
      ]
    }
  ];

  // Función para manejar acciones de la tabla
  const handleAction = (actionKey, row) => {
    if (actionKey === 'ver-detalle-lenceria-page') {
      setSelectedSolicitud(row);
      setIsDetalleModalOpen(true);
    }
  };

  // Función para cerrar modal de detalle
  const handleCloseDetalle = () => {
    setIsDetalleModalOpen(false);
    setSelectedSolicitud(null);
  };

  const fetchSolicitudes = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3000'}/solicitud-prendas-vertical/filter`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          limit: ITEMS_PER_PAGE,
          offset: currentPage,
          query: {
            "edificio.id": edificioFromState?.id
          }
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setDisplayedData(data.data || []);
        setTotalItems(data.totalRecords || 0);
      } else {
        throw new Error(data.message || 'Error al cargar solicitudes');
      }
    } catch (error) {
      console.error('Error al cargar solicitudes:', error);
      toast({
        title: "Error",
        description: "No se pudieron cargar las solicitudes",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, toast]);

  useEffect(() => {
    fetchSolicitudes();
  }, [fetchSolicitudes]);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleNuevaSolicitud = () => {
    setSelectedItems([]);
    setIsCreateModalOpen(true);
  };

  const handleSelectLenceria = (selectedItem) => {
    setSelectedItems(prev => [...prev, selectedItem]);
  };

  const handleSolicitudCreated = async (edificioParam, itemsFromModal) => {
    // Usar items del modal si existen, sino usar selectedItems (flujo antiguo)
    const itemsToSave = itemsFromModal || selectedItems;
    
    // Extraer observación del primer item (todos tienen la misma observación general)
    const observacionGeneral = itemsToSave.length > 0 && itemsToSave[0].observacion 
      ? itemsToSave[0].observacion 
      : '';
    
    if (itemsToSave.length === 0) {
      toast({
        title: "Error",
        description: "Debe seleccionar al menos un elemento",
        variant: "destructive",
      });
      return;
    }

    try {
      const total_solicitado = itemsToSave.reduce((sum, item) => sum + item.cantidad, 0);
      const peso_total = itemsToSave.reduce((sum, item) => sum + (item.peso * item.cantidad), 0);

      // Usar edificio del state si existe, sino usar el parámetro
      const edificioData = edificioFromState || edificioParam;

      if (!edificioData) {
        toast({
          title: "Error",
          description: "No se pudo determinar el portafolio",
          variant: "destructive",
        });
        return;
      }

      // Calcular fecha de entrega (mañana por defecto)
      // Ajustar para zona horaria Colombia (UTC-5) - Sumar 5 horas
      const fechaEntrega = new Date();
      fechaEntrega.setDate(fechaEntrega.getDate() + 1);
      fechaEntrega.setHours(5, 0, 0, 0); // 5 AM UTC = 12 AM Colombia

      const solicitudData = {
        creator: {
          id: user.id,
          nombre: user.name
        },
        edificio: {
          id: edificioData.id || edificioData._id,
          nombre: edificioData.nombre,
          ciudad: edificioData.ciudad,
          direccion: edificioData.direccion
        },
        total_solicitado: total_solicitado,
        total_entregado: 0,
        peso_total: peso_total,
        items: itemsToSave.map(item => ({
          id: item.garmentTypeId || item.id,
          nombre: item.nombre,
          cantidad: item.cantidad,
          peso: item.peso,
          origen: item.origen || 'Manual',
          tipo_origen: item.tipo_origen || 'manual'
        })),
        observaciones: observacionGeneral,
        estado: 'Pendiente',
        fechaEntrega: fechaEntrega.toISOString(),
        tipoLogistica: 'vertical'
      };

      const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3000'}/solicitud-prendas-vertical/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(solicitudData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Error al crear la solicitud');
      }

      toast({
        title: "Solicitud Creada",
        description: "La solicitud de lencería ha sido registrada exitosamente",
      });

      setIsCreateModalOpen(false);
      setSelectedItems([]);
      fetchSolicitudes();
    } catch (error) {
      console.error('Error al crear solicitud:', error);
      toast({
        title: "Error",
        description: error.message || "No se pudo crear la solicitud",
        variant: "destructive",
      });
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.5 }}
      className="space-y-6"
    >
      <Card className="card-gradient-bg shadow-lg">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              {edificioFromState && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate(`/propiedades/hospedajes/${edificioFromState.id}`, { 
                    replace: true,
                    state: { key: Date.now() } // Forzar re-render
                  })}
                  className="mr-2"
                >
                  <ArrowLeft className="h-4 w-4" />
                </Button>
              )}
              <div className="p-3 bg-blue-100 rounded-full">
                <Shirt className="h-8 w-8 text-blue-600" />
              </div>
              <div>
                <CardTitle className="text-2xl font-bold text-foreground">
                  Solicitudes de recolección de lencería
                </CardTitle>
                <CardDescription className="text-muted-foreground">
                  {edificioFromState.nombre} ({edificioFromState.ciudad})
                </CardDescription>
              </div>
            </div>
            <Button onClick={handleNuevaSolicitud}>
              <PlusCircle className="h-4 w-4 mr-2" />
              Nueva Solicitud
            </Button>
          </div>
        </CardHeader>
      </Card>

      <Card>
        <CardContent className="pt-6">
          <DataTable
            columns={columns}
            data={displayedData}
            isLoading={isLoading}
            onAction={handleAction}
          />

          <Pagination
            currentPage={currentPage}
            totalPages={Math.ceil(totalItems / ITEMS_PER_PAGE)}
            onPageChange={handlePageChange}
          />
        </CardContent>
      </Card>

      {/* Modal de Selección */}
      <SelectGarmentTypeModal
        isOpen={isCreateModalOpen}
        onClose={() => {
          setIsCreateModalOpen(false);
          setSelectedItems([]);
        }}
        edificioId={edificioFromState?.id} // Pasar el ID del edificio si existe
        onSelect={handleSelectLenceria}
        onSave={handleSolicitudCreated}
        showSaveButton={true}
      />

      {/* Modal de Detalle de Solicitud */}
      <DetalleSolicitudModal
        isOpen={isDetalleModalOpen}
        onClose={handleCloseDetalle}
        solicitud={selectedSolicitud}
        tipo="lenceria"
      />
    </motion.div>
  );
};

export default SolicitudLenceriaPage;
